import { css } from 'aphrodite';
import * as React from 'react';
import { usePreviewSubscriptionMutation } from '../../../../queries/Admin/Pricebook';
import { Button } from '../../../../web/components-v2/Button';
import { Input } from '../../../../web/components-v2/Input';
import { Modal, IModalProps } from '../../../../web/components/Modal';
import { TaxEstimateData } from '../PricingWizard/types';
import { styleSheet } from './styles';

export interface TaxEstimateModalProps extends Pick<IModalProps, 'isOpen' | 'onRequestClose'> {
	pricebookItemId: string;
	initialData?: Partial<TaxEstimateData>;
	onSuccess: (data: TaxEstimateData) => void;
}

export function TaxEstimateModal({
	isOpen,
	onRequestClose,
	pricebookItemId,
	initialData,
	onSuccess,
}: TaxEstimateModalProps) {
	const [address, setAddress] = React.useState(initialData?.address ?? '');
	const [address2, setAddress2] = React.useState(initialData?.address2 ?? '');
	const [city, setCity] = React.useState(initialData?.city ?? '');
	const [state, setState] = React.useState(initialData?.state ?? '');
	const [zip, setZip] = React.useState(initialData?.zip ?? '');
	const [error, setError] = React.useState<string | null>(null);

	const previewMutation = usePreviewSubscriptionMutation();

	// Reset form when modal opens with new initial data
	React.useEffect(() => {
		if (isOpen) {
			setAddress(initialData?.address ?? '');
			setAddress2(initialData?.address2 ?? '');
			setCity(initialData?.city ?? '');
			setState(initialData?.state ?? '');
			setZip(initialData?.zip ?? '');
			setError(null);
		}
	}, [isOpen, initialData]);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError(null);

		// Validation
		if (!address.trim()) {
			setError('Please enter an address');
			return;
		}
		if (!city.trim()) {
			setError('Please enter a city');
			return;
		}
		if (!state.trim()) {
			setError('Please enter a state');
			return;
		}
		if (!zip.trim()) {
			setError('Please enter a zip code');
			return;
		}

		try {
			const result = await previewMutation.mutateAsync({
				pricebookItemId,
				address: address.trim(),
				address2: address2.trim() || undefined,
				city: city.trim(),
				state: state.trim().toUpperCase(),
				zip: zip.trim(),
			});

			// Convert cents to dollars
			const taxAmount = result.taxInCents / 100;

			onSuccess({
				taxAmount,
				address: address.trim(),
				address2: address2.trim() || undefined,
				city: city.trim(),
				state: state.trim().toUpperCase(),
				zip: zip.trim(),
			});

			onRequestClose();
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Failed to calculate tax');
		}
	};

	const isValid = address.trim() && city.trim() && state.trim() && zip.trim();

	return (
		<Modal isOpen={isOpen} onRequestClose={onRequestClose} useStyledHeader>
			<div className={css(styleSheet.header)}>Calculate Estimated Tax</div>
			<p className={css(styleSheet.subtitle)}>Enter the billing address to calculate estimated sales tax.</p>

			<form className={css(styleSheet.modalBody)} onSubmit={handleSubmit}>
				{error && <div className={css(styleSheet.error)}>{error}</div>}

				{/* Address */}
				<div className={css(styleSheet.formGroup)}>
					<label className={css(styleSheet.label)} htmlFor='tax-address'>
						Address <span className={css(styleSheet.required)}>*</span>
					</label>
					<Input
						id='tax-address'
						onChange={e => setAddress(e.target.value)}
						placeholder='123 Main St'
						required
						styles={[styleSheet.input]}
						type='text'
						value={address}
					/>
				</div>

				{/* Address 2 */}
				<div className={css(styleSheet.formGroup)}>
					<label className={css(styleSheet.label)} htmlFor='tax-address2'>
						Address 2
					</label>
					<Input
						id='tax-address2'
						onChange={e => setAddress2(e.target.value)}
						placeholder='Suite 100 (optional)'
						styles={[styleSheet.input]}
						type='text'
						value={address2}
					/>
				</div>

				{/* City */}
				<div className={css(styleSheet.formGroup)}>
					<label className={css(styleSheet.label)} htmlFor='tax-city'>
						City <span className={css(styleSheet.required)}>*</span>
					</label>
					<Input
						id='tax-city'
						onChange={e => setCity(e.target.value)}
						placeholder='San Francisco'
						required
						styles={[styleSheet.input]}
						type='text'
						value={city}
					/>
				</div>

				{/* State & Zip Row */}
				<div className={css(styleSheet.formRow)}>
					<div className={css(styleSheet.formGroup, styleSheet.formGroupHalf)}>
						<label className={css(styleSheet.label)} htmlFor='tax-state'>
							State <span className={css(styleSheet.required)}>*</span>
						</label>
						<Input
							id='tax-state'
							maxLength={2}
							onChange={e => setState(e.target.value)}
							placeholder='CA'
							required
							styles={[styleSheet.input]}
							type='text'
							value={state}
						/>
					</div>
					<div className={css(styleSheet.formGroup, styleSheet.formGroupHalf)}>
						<label className={css(styleSheet.label)} htmlFor='tax-zip'>
							Zip <span className={css(styleSheet.required)}>*</span>
						</label>
						<Input
							id='tax-zip'
							onChange={e => setZip(e.target.value)}
							placeholder='94102'
							required
							styles={[styleSheet.input]}
							type='text'
							value={zip}
						/>
					</div>
				</div>

				{/* Buttons */}
				<div className={css(styleSheet.buttonRow)}>
					<Button onClick={() => onRequestClose()} type='button' variant='secondary'>
						Cancel
					</Button>
					<Button
						disabled={!isValid || previewMutation.isPending}
						isLoading={previewMutation.isPending}
						type='submit'
						variant='primary'
					>
						Calculate Tax
					</Button>
				</div>
			</form>
		</Modal>
	);
}
